import * as z from 'zod';

/**
 * `GET /vendors/pricing` — the merchant's tariff, shown on the dashboard's
 * "Demand Coefficient" card and (later) the tariffs screen. Shapes captured
 * from a live response.
 */
/**
 * A numeric pricing field. The backend encodes "not configured" as 0 OR null
 * (a tariff-attached merchant answers `price_after_range: null`) — both mean
 * "no value", so null normalizes to 0 instead of throwing at the boundary
 * (which surfaced as an all-zero tariffs screen with no error).
 */
const nullishNumber = z
  .number()
  .nullish()
  .transform((v) => v ?? 0);

export const legacyDynamicPricingSchema = z.object({
  /** Upper bound of this band, in metres. */
  distance: nullishNumber,
  price: nullishNumber,
});

export const legacyBaseRateSchema = z.object({
  /** The base rate's display name — the tariff-name fallback chain's last stop. */
  name: z.string().nullable().default(null),
  courier_call: nullishNumber,
  door_to_door: nullishNumber,
  payment_per_km: nullishNumber,
  fixed_price: nullishNumber,
  /** Distance-banded prices, ascending by `distance`. */
  dynamic_pricings: z.array(legacyDynamicPricingSchema).nullable().default([]),
  /** Surcharge per additional drop-off point. */
  new_point: nullishNumber,
  cancelled_paid_price: nullishNumber,
});

export const legacyWaitTimeSchema = z.object({
  /** Free waiting minutes before per-minute charging starts. */
  free_wait_time: nullishNumber,
  price_per_minute_after: nullishNumber,
});

/**
 * A NAMED tariff, present only for merchants on a negotiated plan (`null` on
 * the reference account). When present it overrides most of `base_rate` — see
 * `toLegacyTariffView`, which encodes the old app's fallback chain.
 */
export const legacyNamedTariffSchema = z.object({
  name: z.string().nullable().default(null),
  /** Included distance, in metres, covered by `price`. */
  range: nullishNumber,
  price: nullishNumber,
  price_per_km_after_range: nullishNumber,
  price_after_range: nullishNumber,
  cancelled_paid_price: nullishNumber,
  free_wait_time: nullishNumber,
  price_per_minute_after_wait: nullishNumber,
  discount: z.number().nullable().default(null),
  discount_valid_from: z.string().nullable().default(null),
  discount_valid_till: z.string().nullable().default(null),
  calculate_discount_for_courier: z.boolean().default(false),
});

export const legacyPricingSchema = z.object({
  base_rate: legacyBaseRateSchema.default(() => legacyBaseRateSchema.parse({})),
  wait_time: legacyWaitTimeSchema.default(() => legacyWaitTimeSchema.parse({})),
  /** The demand multiplier, in basis points of the base rate (250 = ×2.5). */
  time_frame_coefficient: nullishNumber,
  discount: z.number().nullable().default(null),
  discount_valid_from: z.string().nullable().default(null),
  discount_valid_till: z.string().nullable().default(null),
  calculate_discount_for_courier: z.boolean().default(false),
  /** Null unless the merchant is on a negotiated plan. */
  tariff: legacyNamedTariffSchema.nullable().default(null),
  /**
   * Yes, `tarrif` — a TYPO'D SIBLING the old backend also sends and the old
   * screen read as the second stop of its name fallback
   * (`tariff?.name || tarrif?.name || base_rate?.name`). Only the name is
   * consumed.
   */
  tarrif: z
    .object({ name: z.string().nullable().default(null) })
    .nullable()
    .default(null),
});

export type LegacyNamedTariff = z.infer<typeof legacyNamedTariffSchema>;

export type LegacyPricing = z.infer<typeof legacyPricingSchema>;

/**
 * `GET /ratings` — customer reviews, paginated with the same ad-hoc envelope
 * the orders list uses.
 */
/**
 * One preset review tag (`merchant-panel/src/types/requests/ratings-info.ts`).
 * Rendered by the full `/reviews` page as `- comment` rows under the stars.
 */
export const legacyRatingCommentSchema = z.object({
  id: z.number(),
  comment: z.string(),
  is_positive: z.boolean().default(false),
  rated: z.boolean().default(false),
});
export type LegacyRatingComment = z.infer<typeof legacyRatingCommentSchema>;

export const legacyRatingSchema = z.object({
  id: z.number().optional(),
  order_id: z.number().nullable().default(null),
  order_display_id: z.number().nullable().default(null),
  /** 1–5 stars. */
  rate: z.number().default(0),
  /** Preset tags the customer picked — the full page lists them, the dashboard card skips them. */
  comments: z.array(legacyRatingCommentSchema).nullable().default([]),
  /**
   * The customer's free-text review. The wire key is `custom_comment` — an
   * earlier revision modelled it as `comment`, which Zod stripped, so no
   * review text ever rendered.
   */
  custom_comment: z.string().nullable().default(null),
  created_at: z.string().nullable().default(null),
  /** Review photo paths (wire key `photos`), resolved through the file API. */
  photos: z.array(z.string()).nullable().default([]),
});

export const legacyRatingsSchema = z.object({
  data: z.array(legacyRatingSchema).nullable().default([]),
  count: z.number().default(0),
  page_size: z.number().default(0),
  current_page: z.number().default(0),
  last_page: z.number().default(0),
});

export type LegacyRating = z.infer<typeof legacyRatingSchema>;
export type LegacyRatings = z.infer<typeof legacyRatingsSchema>;

/**
 * `GET /vendors/cards` — saved payment cards. The order form only needs the
 * DEFAULT one, to offer "Card *1234" as a payment method.
 *
 * NOTE: unlike every other schema here, this envelope is NOT confirmed against
 * the live API — the reference session had expired when it was written. The
 * old client read `cardsData?.data`, so `{data: [...]}` is the expected shape,
 * but a bare array is accepted too rather than throwing at the boundary and
 * taking the card option away silently. Confirm and tighten when a session is
 * available.
 */
export const legacyCardSchema = z.object({
  id: z.number().optional(),
  /** Masked PAN as the backend stores it, e.g. `8600****1234`. */
  card_number: z.string().nullable().default(null),
  is_default: z.boolean().default(false),
});

export const legacyCardsSchema = z.union([
  z.object({ data: z.array(legacyCardSchema).nullable().default([]) }),
  z.array(legacyCardSchema).transform((data) => ({ data })),
]);

export type LegacyCard = z.infer<typeof legacyCardSchema>;
